import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import { LocationOn } from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';

interface LiveDriverMapProps {
  driverEmail: string;
  driverName?: string;
  deliveryAddress?: string;
  deliveryLatitude?: number;
  deliveryLongitude?: number;
  latitude?: number;
  longitude?: number;
}

const LiveDriverMap: React.FC<LiveDriverMapProps> = ({
  driverEmail,
  driverName = 'Driver',
  deliveryAddress,
  deliveryLatitude,
  deliveryLongitude,
  latitude,
  longitude,
}) => {
  const { t } = useLanguage();
  const [currentLocation, setCurrentLocation] = useState<{ lat: number; lng: number } | null>(
    latitude && longitude ? { lat: latitude, lng: longitude } : null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  useEffect(() => {
    if (!latitude || !longitude) {
      fetchDriverLocation();
    } else {
      setCurrentLocation({ lat: latitude, lng: longitude });
      setIsLoading(false);
    }

    const unsubscribe = setupRealtimeListener();
    const intervalId = setInterval(() => {
      fetchDriverLocation();
    }, 3000);

    return () => {
      unsubscribe();
      clearInterval(intervalId);
    };
  }, [driverEmail, latitude, longitude]);

  const fetchDriverLocation = async () => {
    try {
      const { collection, query, where, getDocs, doc, getDoc } = await import('../services/firestoreCompat');
      const { db } = await import('../services/firebaseConfig');

      const driversRef = collection(db, 'drivers');
      const q = query(driversRef, where('email', '==', driverEmail));
      const driversSnapshot = await getDocs(q);

      if (!driversSnapshot.empty) {
        const driverId = driversSnapshot.docs[0].id;
        const locationDocRef = doc(db, 'driver_locations', driverId);
        const locationDocSnap = await getDoc(locationDocRef);

        if (locationDocSnap.exists()) {
          const locationData = locationDocSnap.data() as any;
          if (locationData.latitude && locationData.longitude) {
            setCurrentLocation({
              lat: locationData.latitude,
              lng: locationData.longitude,
            });
            setLastUpdate(new Date(locationData.timestamp?.toDate?.() || new Date()));
            setError(null);
            setIsLoading(false);
          }
        } else {
          const driverDocRef = doc(db, 'drivers', driverId);
          const driverDocSnap = await getDoc(driverDocRef);
          
          if (driverDocSnap.exists()) {
            const driverData = driverDocSnap.data();
            if (driverData.current_latitude && driverData.current_longitude) {
              setCurrentLocation({
                lat: driverData.current_latitude,
                lng: driverData.current_longitude,
              });
              setError(null);
              setIsLoading(false);
            }
          }
        }
      }
    } catch (err: any) {
      console.error('Error fetching driver location:', err);
      setError(t('drivers.location_load_failed'));
      setIsLoading(false);
    }
  };

  const setupRealtimeListener = () => {
    let unsubscribe: (() => void) | null = null;

    const initListener = async () => {
      try {
        const { collection, query, where, getDocs, doc, onSnapshot } = await import('../services/firestoreCompat');
        const { db } = await import('../services/firebaseConfig');

        const driversRef = collection(db, 'drivers');
        const q = query(driversRef, where('email', '==', driverEmail));
        const driversSnapshot = await getDocs(q);

        if (!driversSnapshot.empty) {
          const driverId = driversSnapshot.docs[0].id;
          const locationDocRef = doc(db, 'driver_locations', driverId);

          unsubscribe = onSnapshot(locationDocRef, (snap) => {
            if (snap.exists()) {
              const data = snap.data() as any;
              if (data.latitude && data.longitude) {
                setCurrentLocation({
                  lat: data.latitude,
                  lng: data.longitude,
                });
                setLastUpdate(data.timestamp?.toDate?.() || new Date());
                setError(null);
              }
            }
          });
        }
      } catch (err) {
        console.error('Error setting up listener:', err);
      }
    };

    initListener();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3 }}>
        <CircularProgress size={40} />
        <Typography variant="body2" sx={{ mt: 2, color: '#666' }}>
          Loading driver location...
        </Typography>
      </Box>
    );
  }

  // Build map URL with both locations if available
  const buildMapUrl = () => {
    if (currentLocation && deliveryLatitude && deliveryLongitude) {
      // Show both rider and delivery location with route
      // Use directions API to show route
      return `https://www.google.com/maps/embed/v1/directions?key=AIzaSyCrKTWODrpA7o-YPI8EsIZbzkzMcRYvc_o&origin=${currentLocation.lat},${currentLocation.lng}&destination=${deliveryLatitude},${deliveryLongitude}&zoom=13`;
    } else if (currentLocation) {
      // Only rider location
      return `https://www.google.com/maps?q=${currentLocation.lat},${currentLocation.lng}&hl=en&z=15&output=embed`;
    } else if (deliveryLatitude && deliveryLongitude) {
      // Only delivery location
      return `https://www.google.com/maps?q=${deliveryLatitude},${deliveryLongitude}&hl=en&z=15&output=embed`;
    }
    return null;
  };

  const mapUrl = buildMapUrl();

  if (error || (!currentLocation && !deliveryLatitude)) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        {error || 'Driver location not available. Make sure the driver has started GPS tracking.'}
      </Alert>
    );
  }

  if (!mapUrl) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        Waiting for location data...
      </Alert>
    );
  }

  return (
    <Box sx={{ width: '100%', height: '400px', mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <LocationOn sx={{ color: '#1976D2', mr: 1 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#000' }}>
            Live Tracking: {driverName}
          </Typography>
        </Box>
        {lastUpdate && (
          <Typography variant="caption" sx={{ color: '#666' }}>
            Last update: {new Date(lastUpdate).toLocaleTimeString()}
          </Typography>
        )}
      </Box>
      
      <Box
        component="iframe"
        key={`${currentLocation?.lat || ''}-${currentLocation?.lng || ''}-${deliveryLatitude || ''}-${deliveryLongitude || ''}-${Date.now()}`}
        src={mapUrl}
        sx={{
          width: '100%',
          height: '100%',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
        }}
        allowFullScreen
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
      />
      
      <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {deliveryAddress && (
          <Typography variant="body2" sx={{ color: '#666' }}>
            <strong>📍 Delivery Address:</strong> {deliveryAddress}
          </Typography>
        )}
        {currentLocation && (
          <Typography variant="caption" sx={{ color: '#999' }}>
            🚗 Rider GPS: {currentLocation.lat.toFixed(6)}, {currentLocation.lng.toFixed(6)}
          </Typography>
        )}
        {deliveryLatitude && deliveryLongitude && (
          <Typography variant="caption" sx={{ color: '#999' }}>
            🏠 Delivery GPS: {deliveryLatitude.toFixed(6)}, {deliveryLongitude.toFixed(6)}
          </Typography>
        )}
      </Box>
      
      <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 1 }}>
        💡 Map updates every 3 seconds. {currentLocation && deliveryLatitude ? 'Shows route from rider to delivery address.' : 'Driver location will move as they deliver the order.'}
      </Typography>
    </Box>
  );
};

export default LiveDriverMap;

